<template>
  <Modal
    width="720"
    :mask-closable="false"
    v-model="visible"
    title="裁切预览"
    :footer-hide="true"
  >
    <div class="cropper">
      <div class="cropper__left">
        <div class="cropper__stage" ref="stage">
          <img ref="img" class="cropper__source" :src="src" :style="imgStyle" draggable="false" @load="onImageLoad" />
          <div
            class="cropper__box"
            :style="boxStyle"
            @mousedown="startDragBox"
          >
            <div class="cropper__handle cropper__handle--tl" @mousedown.stop="startResize('tl', $event)"></div>
            <div class="cropper__handle cropper__handle--tr" @mousedown.stop="startResize('tr', $event)"></div>
            <div class="cropper__handle cropper__handle--bl" @mousedown.stop="startResize('bl', $event)"></div>
            <div class="cropper__handle cropper__handle--br" @mousedown.stop="startResize('br', $event)"></div>
          </div>
        </div>
      </div>
      <div class="cropper__right">
        <div class="cropper__title">头像预览</div>
        <div class="cropper__previews">
          <div class="cropper__preview">
            <img class="cropper__img cropper__img--rounded32" :src="roundedDataUrl" alt="圆角预览" draggable="false">
          </div>
          <div class="cropper__preview">
            <img class="cropper__img cropper__img--circle" :src="circleDataUrl" alt="圆形预览" draggable="false">
          </div>
        </div>
        <div class="cropper__actions">
          <Button @click="onCancel">取消</Button>
          <Button type="primary" @click="onConfirm" :disabled="!circleDataUrl">确定</Button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
export default {
  name: 'IconCropper',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    src: {
      type: String,
      default: ''
    },
    size: {
      type: Number,
      default: 100
    },
    radius: {
      type: Number,
      default: 8
    }
  },
  data () {
    return {
      visible: this.value,
      circleDataUrl: '',
      roundedDataUrl: '',
      lastSrc: '',
      stageRect: { x: 0, y: 0, w: 220, h: 220 },
      imgRect: { x: 0, y: 0, w: 0, h: 0 },
      box: { x: 40, y: 40, w: 140, h: 140 },
      draggingBox: false,
      resizing: '',
      dragStart: { x: 0, y: 0, bx: 0, by: 0, bw: 0, bh: 0, ix: 0, iy: 0 }
    }
  },
  watch: {
    value (val) {
      this.visible = val
    },
    visible (val) {
      this.$emit('input', val)
    }
  },
  computed: {
    boxStyle () {
      return {
        left: this.box.x + 'px',
        top: this.box.y + 'px',
        width: this.box.w + 'px',
        height: this.box.h + 'px'
      }
    },
    imgStyle () {
      return {
        left: this.imgRect.x + 'px',
        top: this.imgRect.y + 'px',
        width: this.imgRect.w + 'px',
        height: this.imgRect.h + 'px',
        transform: 'none'
      }
    }
  },
  methods: {
    onCancel () {
      this.visible = false
    },
    onConfirm () {
      const c = this.circleDataUrl
      const r = this.roundedDataUrl
      if (!c || !r) return
      Promise.all([this.dataUrlToBlob(c), this.dataUrlToBlob(r)]).then(([cb, rb]) => {
        const cf = new File([cb], `icon_circle_${this.size}x${this.size}.png`, { type: 'image/png' })
        const rf = new File([rb], `icon_rounded_${this.size}x${this.size}.png`, { type: 'image/png' })
        this.$emit('select', {
          circle: { dataUrl: c, blob: cb, file: cf },
          rounded: { dataUrl: r, blob: rb, file: rf }
        })
        this.visible = false
      }).catch(() => {
        this.$Message && this.$Message.error('裁切失败，请重试')
      })
    },
    onImageLoad () {
      this.initStage()
      this.resetBox()
      this.preparePreviews()
    },
    initStage () {
      const stage = this.$refs.stage
      const img = this.$refs.img
      const sw = stage.clientWidth
      const sh = stage.clientHeight
      const iw = img.naturalWidth
      const ih = img.naturalHeight
      let dw = sw
      let dh = Math.round(ih * (sw / iw))
      if (dh > sh) {
        dh = sh
        dw = Math.round(iw * (sh / ih))
      }
      const ox = Math.round((sw - dw) / 2)
      const oy = Math.round((sh - dh) / 2)
      this.stageRect = { x: 0, y: 0, w: sw, h: sh }
      this.imgRect = { x: ox, y: oy, w: dw, h: dh }
    },
    resetBox () {
      const side = Math.round(Math.min(this.imgRect.w, this.imgRect.h) * 0.7)
      const bx = this.imgRect.x + Math.round((this.imgRect.w - side) / 2)
      const by = this.imgRect.y + Math.round((this.imgRect.h - side) / 2)
      this.box = { x: bx, y: by, w: side, h: side }
    },
    preparePreviews () {
      if (!this.src) return
      if (this.src === this.lastSrc && this.circleDataUrl && this.roundedDataUrl) return
      this.lastSrc = this.src
      this.circleDataUrl = ''
      this.roundedDataUrl = ''
      window.requestAnimationFrame(() => {
        const img = this.$refs.img
        const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1))
        const circleCanvas = this.renderCircle(img, this.size, dpr)
        const roundedCanvas = this.renderRounded(img, this.size, 32, dpr)
        this.circleDataUrl = circleCanvas.toDataURL('image/png')
        this.roundedDataUrl = roundedCanvas.toDataURL('image/png')
        this.$emit('update', {
          circle: this.circleDataUrl,
          rounded: this.roundedDataUrl
        })
      })
    },
    startDragBox (e) {
      this.draggingBox = true
      this.resizing = ''
      this.dragStart = { x: e.clientX, y: e.clientY, bx: this.box.x, by: this.box.y, bw: this.box.w, bh: this.box.h, ix: this.imgRect.x, iy: this.imgRect.y }
      window.addEventListener('mousemove', this.onDrag)
      window.addEventListener('mouseup', this.endDrag)
    },
    startResize (pos, e) {
      this.draggingBox = false
      this.resizing = pos
      this.dragStart = { x: e.clientX, y: e.clientY, bx: this.box.x, by: this.box.y, bw: this.box.w, bh: this.box.h, ix: this.imgRect.x, iy: this.imgRect.y }
      window.addEventListener('mousemove', this.onDrag)
      window.addEventListener('mouseup', this.endDrag)
    },
    onDrag (e) {
      const dx = e.clientX - this.dragStart.x
      const dy = e.clientY - this.dragStart.y
      const bx = this.dragStart.bx
      const by = this.dragStart.by
      const bw = this.dragStart.bw
      const bh = this.dragStart.bh
      if (this.resizing) {
        let x = bx
        let y = by
        let w = bw
        let h = bh
        if (this.resizing === 'tl') {
          x = bx + dx
          y = by + dy
          w = bw - dx
          h = bh - dy
        } else if (this.resizing === 'tr') {
          y = by + dy
          w = bw + dx
          h = bh - dy
        } else if (this.resizing === 'bl') {
          x = bx + dx
          w = bw - dx
          h = bh + dy
        } else if (this.resizing === 'br') {
          w = bw + dx
          h = bh + dy
        }
        this.setBox(x, y, w, h)
        this.updatePreviews()
      } else if (this.draggingBox) {
        const x = bx + dx
        const y = by + dy
        this.setBox(x, y, bw, bh)
        this.updatePreviews()
      }
    },
    endDrag () {
      window.removeEventListener('mousemove', this.onDrag)
      window.removeEventListener('mouseup', this.endDrag)
      this.draggingBox = false
      this.resizing = ''
      this.preparePreviews()
    },
    setBox (x, y, w, h) {
      const minSize = 20
      const ix = this.imgRect.x
      const iy = this.imgRect.y
      const iw = this.imgRect.w
      const ih = this.imgRect.h
      let size = Math.max(Math.abs(w), Math.abs(h))
      size = Math.max(minSize, size)
      if (size > iw) size = iw
      if (size > ih) size = ih
      if (x < ix) x = ix
      if (y < iy) y = iy
      if (x + size > ix + iw) x = ix + iw - size
      if (y + size > iy + ih) y = iy + ih - size
      this.box = { x: Math.round(x), y: Math.round(y), w: Math.round(size), h: Math.round(size) }
    },
    updatePreviews () {
      const img = this.$refs.img
      const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1))
      const circleCanvas = this.renderCircle(img, this.size, dpr)
      const roundedCanvas = this.renderRounded(img, this.size, 32, dpr)
      this.circleDataUrl = circleCanvas.toDataURL('image/png')
      this.roundedDataUrl = roundedCanvas.toDataURL('image/png')
    },
    createBaseCanvas (size, dpr) {
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(size * dpr)
      canvas.height = Math.round(size * dpr)
      return canvas
    },
    getCropFromBox () {
      const img = this.$refs.img
      const iw = img.naturalWidth
      const ih = img.naturalHeight
      const scaleX = iw / this.imgRect.w
      const scaleY = ih / this.imgRect.h
      const sx = Math.round((this.box.x - this.imgRect.x) * scaleX)
      const sy = Math.round((this.box.y - this.imgRect.y) * scaleY)
      const sw = Math.round(this.box.w * scaleX)
      const sh = Math.round(this.box.h * scaleY)
      return { sx, sy, sw, sh }
    },
    getCenterCropRect (img) {
      const sw = img.naturalWidth || img.width
      const sh = img.naturalHeight || img.height
      const side = Math.min(sw, sh)
      const sx = Math.floor((sw - side) / 2)
      const sy = Math.floor((sh - side) / 2)
      return { sx, sy, side }
    },
    renderCircle (img, size, dpr) {
      const canvas = this.createBaseCanvas(size, 1)
      const ctx = canvas.getContext('2d')
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      const crop = this.getCropFromBox()

      const temp = this.createBaseCanvas(size, dpr)
      const tctx = temp.getContext('2d')
      tctx.imageSmoothingEnabled = true
      tctx.imageSmoothingQuality = 'high'

      const px = temp.width
      const py = temp.height
      const r = Math.min(px, py) / 2
      tctx.save()
      tctx.beginPath()
      tctx.arc(px / 2, py / 2, r, 0, Math.PI * 2)
      tctx.closePath()
      tctx.clip()
      tctx.drawImage(img, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, px, py)
      tctx.restore()

      ctx.drawImage(temp, 0, 0, size, size)
      return canvas
    },
    renderRounded (img, size, radius, dpr) {
      const canvas = this.createBaseCanvas(size, 1)
      const ctx = canvas.getContext('2d')
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'

      const crop = this.getCropFromBox()

      const temp = this.createBaseCanvas(size, dpr)
      const tctx = temp.getContext('2d')
      tctx.imageSmoothingEnabled = true
      tctx.imageSmoothingQuality = 'high'

      const px = temp.width
      const py = temp.height
      const rr = Math.max(0, Math.min(radius * dpr, Math.min(px, py) / 2))
      tctx.save()
      this.roundRectPath(tctx, 0, 0, px, py, rr)
      tctx.clip()
      tctx.drawImage(img, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, px, py)
      tctx.restore()

      ctx.drawImage(temp, 0, 0, size, size)
      return canvas
    },
    roundRectPath (ctx, x, y, w, h, r) {
      ctx.beginPath()
      ctx.moveTo(x + r, y)
      ctx.lineTo(x + w - r, y)
      ctx.quadraticCurveTo(x + w, y, x + w, y + r)
      ctx.lineTo(x + w, y + h - r)
      ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
      ctx.lineTo(x + r, y + h)
      ctx.quadraticCurveTo(x, y + h, x, y + h - r)
      ctx.lineTo(x, y + r)
      ctx.quadraticCurveTo(x, y, x + r, y)
      ctx.closePath()
    },
    dataUrlToBlob (dataUrl) {
      return fetch(dataUrl).then(res => res.blob())
    }
  }
}
</script>

<style lang="scss" scoped>
  .cropper {
    display: flex;
    align-items: flex-start;
    gap: 20px;
  }
  .cropper__left {
    width: 240px;
    height: 240px;
  }
  .cropper__stage {
    position: relative;
    width: 240px;
    height: 240px;
    background: #f0f0f0;
    overflow: hidden;
    user-select: none;
  }
  .cropper__source {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 100%;
    max-height: 100%;
    pointer-events: none;
  }
  .cropper__box {
    position: absolute;
    border: 1px dashed #333;
    box-sizing: border-box;
    background: rgba(255,255,255,0.02);
    cursor: move;
  }
  .cropper__handle {
    position: absolute;
    width: 10px;
    height: 10px;
    background: #fff;
    border: 1px solid #333;
  }
  .cropper__handle--tl { top: -6px; left: -6px; cursor: nwse-resize; }
  .cropper__handle--tr { top: -6px; right: -6px; cursor: nesw-resize; }
  .cropper__handle--bl { bottom: -6px; left: -6px; cursor: nesw-resize; }
  .cropper__handle--br { bottom: -6px; right: -6px; cursor: nwse-resize; }
  .cropper__right {
    flex: 1;
    background: #f7f9fb;
    padding: 12px 16px;
    border-radius: 8px;
  }
  .cropper__title {
    font-size: 13px;
    color: #666;
    margin-bottom: 8px;
  }
  .cropper__previews {
    display: flex;
    gap: 16px;
  }
  .cropper__preview {
    border: 1px solid #e5e5e5;
    background: #fff;
    padding: 12px;
    border-radius: 8px;
  }
  .cropper__img {
    width: 100px;
    height: 100px;
    display: block;
    pointer-events: none;
    user-select: none;
    -webkit-user-drag: none;
    image-rendering: auto;
  }
  .cropper__img--circle {
    border-radius: 50%;
  }
  .cropper__img--rounded32 {
    border-radius: 32px;
  }
</style>



// WEBPACK FOOTER //
// src/components/global/IconCropper.vue